import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./rangeInput.scss";

const MultiRangeSlider = ({ min, max, onChange, id, value, unit = "" }) => {
  const defaultVal = value === null ? [min, max] : value;
  const [minVal, setMinVal] = useState(defaultVal[0]);
  const [maxVal, setMaxVal] = useState(defaultVal[1]);
  const minValRef = useRef();
  const maxValRef = useRef();
  const range = useRef(null);

  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  const handleMinChange = (event, val = null) => {
    let minvalue = val === null ? event?.target?.value : val;
    minvalue = Math.min(Number(minvalue), maxVal - 1);
    const minPercent = getPercent(minvalue);
    const maxPercent = getPercent(maxVal);
    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    setMinVal(minvalue);
  };

  const handleMaxChange = (event, val = null) => {
    let maxvalue = val === null ? event?.target?.value : val;
    maxvalue = Math.max(Number(maxvalue), minVal + 1);
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxvalue);
    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    setMaxVal(maxvalue);
  };

  const handleMinMaxInputChange = (e) => {
    const name = e.target.name;
    if (name === "min") {
      handleMinChange(null, e.target.value);
    } else {
      handleMaxChange(null, e.target.value);
    }
  };

  useEffect(() => {
    onChange({ min: minVal, max: maxVal, key: id });
  }, [minVal, maxVal, id]);

  useEffect(() => {
    handleMinChange(null, minVal);
    handleMaxChange(null, maxVal);
  });

  useEffect(() => {
    minValRef.current.value = "";
    maxValRef.current.value = "";
    if (value === null) {
      handleMinChange(null, min);
      handleMaxChange(null, max);
    } else {
      handleMinChange(null, value[0]);
      handleMaxChange(null, value[1]);
    }
  }, [id]);

  return (
    <>
      <div className="rangeSliderWrapper">
        <input
          type="range"
          min={min}
          max={max}
          value={minVal}
          onChange={handleMinChange}
          className="thumb thumb--left"
          style={{ zIndex: minVal > max - 100 && "5" }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={maxVal}
          onChange={handleMaxChange}
          className="thumb thumb--right"
        />

        <div className="slider" id="slider">
          <div className="slider__track" id="slider__track" />
          <div ref={range} className="slider__range" id="slider__range" />
          <div className="slider__left-value" id="slider__left-value">
            {min}
          </div>
          <div className="slider__right-value" id="slider__right-value">
            {unit === "PPB"
              ? `${max.toLocaleString()} PPB`
              : `${max}${unit === "C" ? `\u00B0 ${unit}` : ` ${unit}`}`}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between mt-5">
        <div className="d-flex align-items-center mt-3 minMaxInput">
          <span className="from-to-text">From:</span>
          <div className="inputWrapper">
            <input
              id="from_input"
              type="text"
              name="min"
              value={Number(minVal).toLocaleString()}
              ref={minValRef}
              placeholder="From"
              onChange={handleMinMaxInputChange}
            />
          </div>
          <span
            className={classNames("unit", {
              ppb: unit === "PPB",
            })}
          >
            {unit === "C" ? `\u00B0 ${unit}` : unit}
          </span>
        </div>
        <div className="d-flex align-items-center mt-3 minMaxInput">
          <span className="from-to-text">To:</span>
          <div className="inputWrapper">
            <input
              id="to_input"
              type="text"
              name="max"
              value={Number(maxVal).toLocaleString()}
              ref={maxValRef}
              placeholder="To"
              onChange={handleMinMaxInputChange}
            />
          </div>
          <span
            className={classNames("unit", {
              ppb: unit === "PPB",
            })}
          >
            {unit === "C" ? `\u00B0 ${unit}` : unit}
          </span>
        </div>
      </div>
    </>
  );
};

MultiRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default MultiRangeSlider;
